/**
 * input.tsx
 * ─────────────────────────────────────────────────────────────
 * Cognisun CRM — Standard Input Component
 *
 * DESIGN CONTRACT (all states):\
 *   Height:      40px  (h-10)          ← enforced by theme.css !important
 *   Radius:      10px  (rounded-[10px]) ← --ds-radius-control
 *   Border:      1px solid #E2E8F0
 *   Background:  #FFFFFF
 *   Text:        13px, #0F172A
 *   Placeholder: #9CA3AF
 *   Padding:     px-3 py-2  (12px h / 8px v)
 *   Shadow:      0 1px 2px rgba(0,0,0,0.04) — uniform subtle elevation
 *
 * STATES:
 *   default  → border #E2E8F0  + subtle shadow
 *   hover    → border #CBD5E1  (minimal border shift — shadow unchanged)
 *   focus    → border #008ED3  + base shadow + thin primary glow ring
 *   disabled → bg #F8FAFC, text #9CA3AF, border #E2E8F0, opacity-60
 *   error    → border #EF4444 + ring-1 ring-[#EF4444]/15
 *
 * VARIANTS:
 *   default      → plain input
 *   hasSearchIcon→ prepends a magnifier icon (left-3)
 */

import * as React from "react";
import { Search } from "lucide-react";
import { cn } from "./utils";

interface InputProps extends React.ComponentProps<"input"> {
  hasSearchIcon?: boolean;
}

// ─── Shared base classes ──────────────────────────────────────
const BASE = [
  // Layout
  "flex h-10 w-full min-w-0",
  // Shape
  "rounded-[10px]",
  // Border
  "border border-[#E2E8F0]",
  // Background & text
  "bg-white",
  "text-[13px] font-normal text-[#0F172A]",
  "placeholder:text-[#9CA3AF]",
  // Internal padding
  "px-3 py-2",
  // Subtle elevation — same as every other input/select in the system
  "shadow-[0_1px_2px_rgba(0,0,0,0.04)]",
  // Interaction
  "cursor-text outline-none",
  // Transitions — shadow intentionally excluded from transition so it never "grows" on hover
  "transition-[border-color,background-color] duration-150",
  // ── States ──
  // Hover: minimal border shift — no shadow change
  "hover:border-[#CBD5E1]",
  // Focus: primary blue border + base shadow + thin glow ring
  "focus:border-[#008ED3]",
  "focus:shadow-[0_1px_2px_rgba(0,0,0,0.04),0_0_0_2px_rgba(0,142,211,0.14)]",
  "focus:ring-0",
  "focus:bg-white",
  // Disabled: muted bg + text + locked border
  "disabled:bg-[#F8FAFC] disabled:text-[#9CA3AF]",
  "disabled:border-[#E2E8F0] disabled:cursor-not-allowed disabled:opacity-60",
  "disabled:pointer-events-none",
  // File input
  "file:inline-flex file:h-7 file:border-0 file:bg-transparent",
  "file:text-[13px] file:font-medium file:text-[#0F172A]",
  // Error (aria-invalid)
  "aria-invalid:border-[#EF4444] aria-invalid:shadow-[0_1px_2px_rgba(0,0,0,0.04),0_0_0_2px_rgba(239,68,68,0.14)]",
  // Selection highlight
  "selection:bg-[#008ED3]/20 selection:text-[#0F172A]",
].join(" ");

function Input({ className, type, hasSearchIcon = false, ...props }: InputProps) {
  if (hasSearchIcon) {
    return (
      <div className="relative w-full">
        <Search
          className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-[#9CA3AF] pointer-events-none"
          aria-hidden="true"
        />
        <input
          type={type}
          data-slot="input"
          data-variant="search"
          className={cn(BASE, "pl-10 pr-3", className)}
          {...props}
        />
      </div>
    );
  }

  return (
    <input
      type={type}
      data-slot="input"
      className={cn(BASE, className)}
      {...props}
    />
  );
}

export { Input };